import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { History } from 'lucide-react'
import { api, messageErreur } from '../../lib/api'
import type { Dossier, VersionIdentite } from '../../lib/types'
import { Champ, Chargement, Message, Modale } from '../base'
import * as f from '../../lib/format'
import { SEXES } from '@doc/shared'

export default function Identite({
  patient,
  fermer,
}: {
  patient: Dossier['patient']
  fermer: () => void
}) {
  const client = useQueryClient()
  const [valeurs, setValeurs] = useState({
    nom: patient.nom ?? '',
    nomNaissance: patient.nomNaissance ?? '',
    prenom: patient.prenom ?? '',
    dateNaissance: f.pourChampDate(patient.dateNaissance),
    sexe: patient.sexe ?? 'NON_RENSEIGNE',
    telephone: patient.telephone ?? '',
    email: patient.email ?? '',
    adresse: patient.adresse ?? '',
    notes: patient.notes ?? '',
  })
  const [erreur, setErreur] = useState<string | null>(null)
  const [historique, setHistorique] = useState(false)

  const enregistrer = useMutation({
    mutationFn: () => api(`/api/patients/${patient.id}`, { methode: 'PATCH', corps: valeurs }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['dossier', patient.id] })
      client.invalidateQueries({ queryKey: ['patients'] })
      fermer()
    },
    onError: (e) => setErreur(messageErreur(e)),
  })

  const champ = (cle: keyof typeof valeurs) => ({
    value: valeurs[cle],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setValeurs((v) => ({ ...v, [cle]: e.target.value })),
  })

  return (
    <Modale
      titre="Identité du dossier"
      sousTitre={`Référence ${patient.reference} — elle ne change jamais, même si l'identité est corrigée.`}
      fermer={fermer}
      piedDePage={
        <>
          <button className="bouton-discret mr-auto" onClick={() => setHistorique(true)}>
            <History className="h-4 w-4" />
            Historique ({patient.nbVersionsIdentite})
          </button>
          <button className="bouton-discret" onClick={fermer}>
            Annuler
          </button>
          <button
            className="bouton-primaire"
            onClick={() => enregistrer.mutate()}
            disabled={enregistrer.isPending}
          >
            Enregistrer
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Champ etiquette="Nom">
          <input className="champ" {...champ('nom')} />
        </Champ>
        <Champ etiquette="Nom de naissance">
          <input className="champ" {...champ('nomNaissance')} />
        </Champ>
        <Champ etiquette="Prénom">
          <input className="champ" {...champ('prenom')} />
        </Champ>
        <Champ etiquette="Date de naissance">
          <input className="champ" type="date" {...champ('dateNaissance')} />
        </Champ>
        <Champ etiquette="Sexe">
          <select
            className="champ"
            value={valeurs.sexe}
            onChange={(e) => setValeurs((v) => ({ ...v, sexe: e.target.value }))}
          >
            {Object.entries(SEXES).map(([cle, libelle]) => (
              <option key={cle} value={cle}>
                {libelle}
              </option>
            ))}
          </select>
        </Champ>
        <Champ etiquette="Téléphone">
          <input className="champ" {...champ('telephone')} />
        </Champ>
        <Champ etiquette="Adresse e-mail">
          <input className="champ" {...champ('email')} />
        </Champ>
        <Champ etiquette="Adresse postale" className="sm:col-span-2">
          <input className="champ" {...champ('adresse')} />
        </Champ>
        <Champ
          etiquette="Notes sur l'identité"
          aide="Rien d'obligatoire : un dossier peut rester sans identité."
          className="sm:col-span-2"
        >
          <textarea className="champ min-h-[64px]" {...champ('notes')} />
        </Champ>

        {erreur && (
          <div className="sm:col-span-2">
            <Message ton="critique">{erreur}</Message>
          </div>
        )}
      </div>

      {historique && (
        <HistoriqueIdentite patientId={patient.id} fermer={() => setHistorique(false)} />
      )}
    </Modale>
  )
}

const ACTIONS: Record<string, string> = {
  creation: 'Création du dossier',
  correction: 'Correction',
  fusion: 'Après fusion de dossiers',
}

const ETIQUETTES: Record<string, string> = {
  reference: 'Référence',
  nom: 'Nom',
  nomNaissance: 'Nom de naissance',
  prenom: 'Prénom',
  dateNaissance: 'Date de naissance',
  sexe: 'Sexe',
  telephone: 'Téléphone',
  email: 'E-mail',
  adresse: 'Adresse',
  notes: 'Notes',
}

function HistoriqueIdentite({ patientId, fermer }: { patientId: string; fermer: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['identite-versions', patientId],
    queryFn: () =>
      api<{ versions: VersionIdentite[] }>(`/api/patients/${patientId}/identite/versions`),
  })

  return (
    <Modale
      titre="Historique de l’identité"
      sousTitre="Chaque correction garde l'état précédent, son auteur et sa date."
      large
      fermer={fermer}
    >
      {isLoading || !data ? (
        <Chargement />
      ) : (
        <ol className="flex flex-col gap-3">
          {data.versions.map((version) => (
            <li key={version.id} className="rounded-lg bg-slate-50 px-4 py-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[13px] font-medium">
                  {ACTIONS[version.action] ?? version.action}
                </span>
                <span className="text-[12px] text-brume">
                  {f.heure(version.creeLe)}
                  {version.auteur ? ` · ${version.auteur}` : ''}
                </span>
              </div>
              <dl className="mt-2 grid grid-cols-2 gap-x-5 gap-y-1 text-[12.5px] sm:grid-cols-3">
                {Object.entries(version.contenu)
                  .filter(([, valeur]) => valeur !== null && valeur !== '')
                  .map(([cle, valeur]) => (
                    <div key={cle}>
                      <dt className="text-brume">{ETIQUETTES[cle] ?? cle}</dt>
                      <dd className="text-encre">
                        {cle === 'dateNaissance'
                          ? f.dateCourte(String(valeur))
                          : String(valeur)}
                      </dd>
                    </div>
                  ))}
              </dl>
            </li>
          ))}
        </ol>
      )}
    </Modale>
  )
}
